/**
 * @fileoverview Shape `/subscription/usage` into what a usage meter renders (X10).
 *
 * An account's recording minutes come from up to three places, and they are
 * three genuinely different promises:
 *
 *   1. the MONTHLY allowance — a ceiling that refills on a rolling 30-day window
 *   2. ONE-TIME minutes — granted, never bought, and they never come back
 *      (the signup grant, migration goodwill, a promo)
 *   3. PURCHASED minutes — bought top-ups
 *
 * They are spent in exactly that order (money last), which is why they cannot
 * be folded into one number: "how many starter minutes do I have left?" becomes
 * unanswerable the moment anything is spent, and a user watching a bought
 * balance sit still while they record has no way to tell working from broken.
 *
 * The visual split follows the semantic one. The monthly allowance gets a BAR
 * because it has a denominator that resets — a cycle. The other two get plain
 * BALANCES because they only ever go down. Drawing a one-time grant as a
 * progress bar would quietly promise a refill that never arrives, which is the
 * single most expensive misreading available here.
 *
 * Credits extend the PERSONAL bucket only (`usage.credit_minutes_for_context`
 * server-side): they belong to the instructor, so spending them on an org
 * seat's quota would spend a person's money on an organisation's allowance.
 * Org contexts therefore render a bare monthly bar and no pools.
 *
 * Mirrored in `mobile/src/utils/usageMeter.js` — the two must agree, or the
 * same account reads differently on a phone and a laptop. Both suites run the
 * same fixture table.
 */

const HOUR = 60;

/** Labels for the two non-renewing pools. Kept here (not in the components) so
 *  web and mobile cannot drift into describing the same balance differently. */
export const POOL_LABELS = {
  allowance: "One-time minutes",
  topup: "Purchased minutes",
};

/**
 * Render a minute count for a human.
 *
 * Balances are often small — a nearly-spent grant reading "0.1 hrs" tells
 * nobody anything — so anything under an hour is shown in minutes. The monthly
 * bar keeps both of its sides in hours regardless: it is a ratio, and
 * "45 min / 40.0 hrs" would be worse than either unit alone.
 *
 * @param {number} minutes
 * @returns {string} e.g. "45 min" or "3.0 hrs"
 */
export const formatMinutes = (minutes) => {
  const value = Math.max(0, Number(minutes) || 0);
  const whole = Math.round(value);
  if (whole < HOUR) return `${whole} min`;
  return `${(value / HOUR).toFixed(1)} hrs`;
};

/**
 * The one line of explanation under a context's meter.
 *
 * @param {object|null} monthly
 * @param {Array} pools
 * @param {object} ctx
 * @returns {string}
 */
const noteFor = (monthly, pools, ctx) => {
  if (!monthly && !pools.length) return "No AI recording minutes available.";
  // A POOLED org context (O4) is a shared balance, and it has to say so: an
  // instructor watching hours they did not record disappear needs to read that
  // as a colleague teaching, not as a bug. It also resets with the calendar
  // month rather than on a rolling window, because the org is funded per
  // active student per billing month.
  if (ctx && ctx.pooled) {
    const students = Number(ctx.billed_students) || 0;
    const hours = Number(ctx.hours_per_student) || 0;
    const sized = students && hours ? ` (${hours} hrs \u00d7 ${students} students)` : "";
    return `Shared across everyone at this organization${sized}. Resets at the start of each month.`;
  }
  if (!pools.length) return "Resets on a rolling 30-day window.";
  if (!monthly) return "These don't expire, and they don't renew.";
  // Both present: the spend order is the thing worth saying. Without it, a
  // balance that correctly sits still reads as a bug.
  return "Monthly hours are used first. Your other minutes don't expire.";
};

/**
 * @param {object} ctx One entry of the payload's `contexts` array.
 * @param {number} allowanceMinutes Personal one-time balance.
 * @param {number} topupMinutes Personal purchased balance.
 * @returns {object} A context ready to render.
 */
const buildContext = (ctx, allowanceMinutes, topupMinutes) => {
  const isPersonal = ctx.context === "personal";
  const usedMinutes = Math.max(0, Number(ctx.minutes_used) || 0);
  const limitMinutes = Math.max(0, Number(ctx.minutes_limit) || 0);

  // No bar without a denominator. Since I3 the free tier's monthly allowance is
  // zero, and a 0-limit bar renders as "42.5 / 0.0 hrs · 0%" — which says the
  // account has nothing while a 180-minute grant sits unspent beside it.
  const monthly =
    limitMinutes > 0
      ? {
          usedMinutes,
          limitMinutes,
          remainingMinutes: Math.max(0, limitMinutes - usedMinutes),
        }
      : null;

  const pools = isPersonal || ctx.accounting === "recording_attempts"
    ? [
        { key: "allowance", label: POOL_LABELS.allowance, minutes: isPersonal ? allowanceMinutes : Number(ctx.allowance_minutes) || 0 },
        { key: "topup", label: POOL_LABELS.topup, minutes: isPersonal ? topupMinutes : Number(ctx.topup_minutes) || 0 },
      ].filter((pool) => pool.minutes > 0)
    : [];

  const sources = (monthly ? 1 : 0) + pools.length;
  const totalMinutes =
    (monthly ? monthly.remainingMinutes : 0) + pools.reduce((sum, pool) => sum + pool.minutes, 0);

  return {
    key: ctx.context,
    label: isPersonal ? "Your recording hours" : `${ctx.org_name || "Organization"} program hours`,
    monthly,
    pools,
    totalMinutes,
    // One source already states its own total; repeating it above would be
    // noise. Zero sources still needs a number, or the card renders empty.
    showTotal: sources !== 1,
    exhausted: sources === 0,
    pooled: !!ctx.pooled,
    note: noteFor(monthly, pools, ctx),
    accountingNote: ctx.accounting === "recording_attempts"
      ? `${formatMinutes(ctx.minutes_consumed || 0)} processed · ${formatMinutes(ctx.minutes_reserved || 0)} reserved · ${formatMinutes(ctx.minutes_credited || 0)} returned this window. Re-recordings count as new processing.`
      : null,
  };
};

/**
 * Turn a `/subscription/usage` payload into the meter's render model.
 *
 * @param {object|null} usage
 * @returns {{contexts: Array}}
 */
export const buildUsageModel = (usage) => {
  if (!usage) return { contexts: [] };

  const allowance = Math.max(0, Number(usage.allowance_minutes) || 0);
  const creditTotal = Math.max(0, Number(usage.credit_minutes) || 0);
  // `credit_minutes` is the sum and is the ceiling the recording gate uses, so
  // deriving the remainder means a payload without the pool split can only
  // mislabel minutes, never hide them. Showing less than the gate allows is the
  // one failure worth ruling out.
  const topup =
    usage.topup_minutes == null
      ? Math.max(0, creditTotal - allowance)
      : Math.max(0, Number(usage.topup_minutes) || 0);

  const contexts = usage.contexts?.length
    ? usage.contexts
    : [
        {
          context: "personal",
          org_name: null,
          minutes_used: usage.minutes_used,
          minutes_limit: usage.minutes_limit,
        },
      ];

  return { contexts: contexts.map((ctx) => buildContext(ctx, allowance, topup)) };
};

/**
 * Below this many minutes left, the recording screen warns.
 */
export const LOW_MINUTES_THRESHOLD = 10;

/**
 * The "you are running low" line for the recording screen, or null when there
 * is nothing to say.
 *
 * Lives here, beside the meter, because it answers the meter's question and
 * had been answering it WRONGLY in both clients: the banner read
 * `minutes_remaining` alone — the monthly allowance remainder — and ignored
 * the credit balance entirely. Since I3 the free tier's monthly allowance is
 * zero and its whole entitlement is a 180-minute grant held as credit, so a
 * brand-new instructor was told "you've reached your monthly recording limit"
 * and offered an upgrade while every one of those 180 minutes sat unspent and
 * the recording gate was perfectly willing to let them record.
 *
 * Two consequences follow, and both are why the trigger moved rather than just
 * the wording. It counts the SAME total the gate does (allowance headroom plus
 * credit), so the warning appears when recording is actually about to stop.
 * And it only says "this month" when some of that total genuinely refills —
 * promising a renewal to an account whose minutes were granted once is the
 * more expensive half of the same mistake.
 *
 * @param {object|null} usage A `/subscription/usage` payload.
 * @returns {string|null}
 */
export const lowMinutesWarning = (usage) => {
  if (!usage) return null;
  const { contexts } = buildUsageModel(usage);
  const personal = contexts.find((ctx) => ctx.key === "personal") || contexts[0];
  if (!personal) return null;

  const { totalMinutes, monthly } = personal;
  if (totalMinutes > LOW_MINUTES_THRESHOLD) return null;

  if (totalMinutes <= 0) {
    return monthly
      ? "You've used all your recording minutes for this month."
      : "You've used all your AI recording minutes.";
  }
  const left = Math.round(totalMinutes);
  return monthly
    ? `Only ${left} minutes left this month.`
    : `Only ${left} minutes of AI recording left.`;
};

export default buildUsageModel;
